import React, { FC } from 'react'
import { Helmet } from 'react-helmet'
import styled from 'styled-components'

//* utils
import { ModuleNames } from '../../../constants/componentNames.constants'
import { ComponentFeaturedSlider } from '../../../models/components/featuredSlider'

//* components
import Benefits from '../../global/benefits'
import CardSlider from '../../global/cardSlider'
import CollapsibleQuadrant from '../../global/collapsibleQuadrant'
import ContentBlock from '../../global/contentBlock'
import ContentColumns from '../../global/contentColumns'
import ContentRows from '../../global/contentRows'
import Divider from '../../global/divider'
import FeaturedResource from '../../global/featuredResource'
import FeaturedSlider from '../../global/featuredSlider'
import FeaturedTiles from '../../global/featuredTiles'
import FlexibleHero from '../../global/flexibleHero'
import Grid from '../../global/grid'
import Hero from '../../global/hero'
import HeroTabs from '../../global/heroTabs'
import Icons from '../../global/icons'
import KeyStats from '../../global/keyStats'
import MainCTA from '../../global/mainCta'
import MediaText from '../../global/mediaText'
import ParagraphBlock from '../../global/paragraphBlock'
import PlatformDiagram from '../../global/platformDiagram'
import Quote from '../../global/quote'
import RawHTML from '../../global/rawHTML'
import ResourceRow from '../../global/resourceRow'
import ResourceTabs from '../../global/resourceTabs'
import SEOTags from '../../global/seoTags'
import SliderComponent from '../../global/slider'
import Tables from '../../global/table'
import ThreeTiles from '../../global/threeTiles'
import Tiles from '../../global/tiles'
import VideoSpotlight from '../../global/videoSpotlight'

type Props = {
  data: any
  locale: string
  path: string
  featuredSlider: ComponentFeaturedSlider | null
  location: any
}

const PageContainer = styled.section<{ $css: string }>`
  ${({ $css }) => $css}
`

export const Modules: FC<Props> = ({ data, locale, path, featuredSlider, location }) => {
  const bodyJs = typeof data.bodyJs === 'string' ? data.bodyJs : data.bodyJs?.bodyJs
  const bodyCss = typeof data.bodyCss === 'string' ? data.bodyCss : data.bodyCss?.bodyCss

  let pageTitle: string = ''
  pageTitle = data?.pageName || data?.name

  const dynamicPageContent = data.flexSection?.map((module: any, i: number) => {
    switch (module?.__typename) {
      case ModuleNames.BENEFITS:
        return <Benefits data={module} key={`page-component--${i}`} />

      case ModuleNames.COLLAPSIBLE_QUADRANT:
        return <CollapsibleQuadrant data={module} key={`page-component--${i}`} />

      case ModuleNames.GLOBAL_CONTENT_BLOCK:
        return <ContentBlock data={module} key={`page-component--${i}`} />

      case ModuleNames.GLOBAL_CONTENT_COLUMNS:
        return <ContentColumns data={module} pageType="rawHTML" key={`page-component--${i}`} />

      case ModuleNames.GLOBAL_CONTENT_BLOCK:
        return <ContentBlock data={module} key={`page-component--${i}`} />

      case ModuleNames.CONTENT_ROWS:
        return <ContentRows data={module} key={`page-component--${i}`} />

      case ModuleNames.DIVIDER:
        return <Divider data={module} key={`page-component--${i}`} />

      case ModuleNames.FEATURED_RESOURCE:
        return (
          <FeaturedResource
            data={module}
            pageLocale={locale}
            pageType="rawHTML"
            key={`page-component--${i}`}
          />
        )

      case ModuleNames.FLEXIBLE_HERO:
        return <FlexibleHero data={module} key={`page-component--${i}`} />

      case ModuleNames.GRID:
        return <Grid data={module} key={`page-component--${i}`} />

      case ModuleNames.HERO:
        return (
          <Hero
            data={module}
            location={location}
            pageTitle={pageTitle}
            showBreadcrumbs={true}
            key={`page-component--${i}`}
            breadcrumb={module?.breadcrumb}
            locale={locale}
            pageType="rawHTML"
          />
        )

      case ModuleNames.HERO_TABS:
        return <HeroTabs data={module} key={`page-component--${i}`} />

      case ModuleNames.ICONS:
        return <Icons data={module} key={`page-component--${i}`} />

      case ModuleNames.GLOBAL_KEY_STATS:
        return <KeyStats data={module} key={`page-component--${i}`} />

      case ModuleNames.GLOBAL_MAIN_CTA:
        return <MainCTA data={module} locale={locale} key={`page-component--${i}`} />

      case ModuleNames.FEATURED_TILES:
        return <FeaturedTiles data={module} key={`page-component--${i}`} />

      case ModuleNames.MEDIA_ASSET:
        return <MediaText data={module} hasMediaAsset={true} key={`page-component--${i}`} />

      case ModuleNames.MEDIA_TEXT:
        return <MediaText data={module} key={`page-component--${i}`} />

      case ModuleNames.PARAGRAPH_BLOCK:
        return <ParagraphBlock data={module} key={`page-component--${i}`} />

      case ModuleNames.PLATFORM_DIAGRAM:
        return <PlatformDiagram data={module} key={`page-component--${i}`} />

      case ModuleNames.QUOTE:
        return <Quote data={module} key={`page-component--${i}`} />

      case ModuleNames.RAW_HTML:
        return <RawHTML data={module} key={`page-component--${i}`} />

      case ModuleNames.RESOURCE_ROW:
        return (
          <ResourceRow
            data={module}
            pageType="rawHTML"
            key={`page-component--${i}`}
            locale={locale}
          />
        )

      case ModuleNames.RESOURCE_TABS:
        return <ResourceTabs data={module} key={`page-component--${i}`} />

      case ModuleNames.SLIDER:
        return <SliderComponent data={module} key={`page-component--${i}`} />

      case ModuleNames.TABLE:
        return <Tables data={module} key={`page-component--${i}`} />

      case ModuleNames.RESOURCE_TABS:
        return <SliderComponent data={module} key={`page-component--${i}`} />

      case ModuleNames.TILES:
        return <Tiles data={module} key={`page-component--${i}`} />

      case ModuleNames.THREETILES:
        return <ThreeTiles data={module} key={`page-component--${i}`} />

      case ModuleNames.VIDEO_SPOTLIGHT:
        return <VideoSpotlight data={module} key={`page-component--${i}`} />

      case ModuleNames.CARD_SLIDER:
        return <CardSlider data={module} key={`page-component--${i}`} />

      default:
        return null
    }
  })

  return (
    <PageContainer data-cy="RawHTMLPage" $css={bodyCss}>
      {bodyJs && (
        <Helmet>
          <script>{bodyJs}</script>
        </Helmet>
      )}

      <SEOTags seoMetadata={data.seoMetadata} slug={data.slug} path={path} locale={locale} />

      {featuredSlider && (
        <FeaturedSlider location={location} featuredSliderData={featuredSlider} slug={data.slug} />
      )}

      {dynamicPageContent}
    </PageContainer>
  )
}
